import React, {
  useState,
  useEffect,
  type ChangeEvent,
  type FormEvent,
} from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  InputAdornment,
  MenuItem,
  type SelectChangeEvent,
  Divider,
  useTheme,
  IconButton,
  Avatar,
} from "@mui/material";
import {
  Check,
  Close as CloseIcon,
  Badge as RoleIcon,
  PersonOutline,
  LocationOn,
  Public,
  StickyNote2,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { tokens } from "../../../../theme";
import type { RootState } from "../../../../store/Index";
import apiService from "../../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../components/common/ShowToast";
import CustomTextField from "../../../../components/common/Textfield";
import PhoneNumberField from "../../../../components/common/PhoneNumberField";
import PhotoEditor from "../../../../components/PhotoEditor";
import { MuiTelInputInfo } from "mui-tel-input";
import { IContactData } from "../../types/venues.types";

interface IContactResponse {
  id: number;
  [key: string]: unknown;
}

interface IEditIndividualContactProps {
  contact: IContactData;
  onComplete: (updatedContact: IContactData) => void;
  onCancel: () => void;
}

// ── Section header helper ───────────────────────────────────────────────
const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  accent: string;
}> = ({ icon, title, accent }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: "10px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: `${accent}1A`,
        color: accent,
      }}
    >
      {icon}
    </Box>
    <Typography variant="subtitle1" fontWeight={700}>
      {title}
    </Typography>
  </Box>
);

const DialogEditVenueIndividualContacts: React.FC<
  IEditIndividualContactProps
> = ({ contact, onComplete, onCancel }) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, reduxTheme);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");

  const [form, setForm] = useState<IContactData>({
    ...contact,
    email: contact.email ?? "",
    phone: contact.phone ?? "",
    role: contact.role ?? "",
    website: contact.website ?? "",
    facebook_profile: contact.facebook_profile ?? "",
    instagram_profile: contact.instagram_profile ?? "",
    twitter_profile: contact.twitter_profile ?? "",
    linkedin_profile: contact.linkedin_profile ?? "",
    notes: contact.notes ?? "",
    contactImageFile: null,
  });

  const validateEmail = (email: string): string => {
    if (!email) return "";
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!regex.test(email)) return "Invalid email format.";
    if (email.startsWith(".")) return "Email cannot start with a dot.";
    if (email.endsWith(".")) return "Email cannot end with a dot.";
    if (email.split("@")[0].endsWith("."))
      return "Local part cannot end with a dot.";
    return "";
  };

  useEffect(() => {
    return () => {
      if (form.contact_image && form.contact_image.startsWith("blob:")) {
        URL.revokeObjectURL(form.contact_image);
      }
    };
  }, [form.contact_image]);

  const handleChange = (
    e:
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>,
  ) => {
    const { name, value } = e.target as HTMLInputElement;
    if (name === "email") {
      const error = validateEmail(value);
      setEmailError(error);
      setForm((prev) => ({ ...prev, email: value }));
    } else {
      setForm((prev) => ({ ...prev, [name as keyof IContactData]: value }));
    }
  };

  const handleSelectChangeWrapper = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    handleChange(event as ChangeEvent<HTMLInputElement>);
  };

  const handlePhoneInputChange = (value: string, _info: MuiTelInputInfo) => {
    void _info;
    setForm((prev) => ({ ...prev, phone: value }));
  };

  const handleSaveContactImage = async (
    imageData: string,
    contactId?: number,
  ) => {
    try {
      const response = await fetch(imageData);
      const blob = await response.blob();
      const file = new File([blob], `contact_image_${contactId || "new"}.jpg`, {
        type: blob.type,
      });
      setForm((prev) => ({
        ...prev,
        contact_image: imageData,
        contactImageFile: file,
      }));
      showSuccessToast("Contact image saved successfully!");
    } catch (error) {
      console.error("Failed to process contact image:", error);
      showErrorToast("Failed to process contact image. Please try again.");
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!form.first_name || !form.last_name) {
      showErrorToast("First name and last name are required.");
      setIsSubmitting(false);
      return;
    }

    if (!form.email && !form.phone) {
      showErrorToast(
        "Provide at least one contact method (email or phone number).",
      );
      setIsSubmitting(false);
      return;
    }

    if (emailError) {
      showErrorToast("Please correct the email address error.");
      setIsSubmitting(false);
      return;
    }

    const formData = new FormData();
    formData.append("contact_type", form.contact_type);
    Object.entries(form).forEach(([key, value]) => {
      if (key === "contactImageFile" && value instanceof File) {
        formData.append("contact_image", value);
      } else if (key !== "contact_image" && value !== null && value !== "") {
        formData.append(key, value.toString());
      }
    });

    try {
      await apiService.patch<IContactResponse>(
        `/api/contacts/contact/${form.id}/`,
        formData,
        false,
      );

      showSuccessToast("Individual contact updated successfully.");

      const updatedContact: IContactData = { ...form }; // include all current form fields
      onComplete(updatedContact);
    } catch (err) {
      console.error("Failed to update individual contact:", err);
      let errorMessage =
        "Failed to update individual contact. Please try again.";
      const errorData = (
        err as { response?: { data?: Record<string, unknown> } }
      )?.response?.data;
      if (errorData) {
        errorMessage =
          Object.values(errorData).flat().join(" ") || errorMessage;
      }
      showErrorToast(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isEmailValid = !emailError && (form.email ?? "").includes("@");
  const accent = colors.primary[400];

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      bgcolor: colors.neutral?.[800] ?? "transparent",
      "& fieldset": { borderColor: colors.primary[300] },
      "&:hover fieldset": {
        borderColor: colors.primary[300],
      },
      "&.Mui-focused fieldset": {
        borderColor: accent,
        borderWidth: "1.5px",
      },
    },
    "& .MuiInputLabel-root": {
      color: colors.primary[300],
    },
    "& .MuiInputBase-input": {
      color: colors.neutral[100],
    },
  };

  return (
    <Box sx={{ background: colors.neutral[900] }}>
      {/* ── Header banner ── */}
      <Box
        sx={{
          position: "relative",
          px: 4,
          pt: 3.5,
          pb: 3,
          background: `linear-gradient(135deg, ${accent} 0%, ${colors.primary[600] ?? accent} 100%)`,
          color: "#fff",
        }}
      >
        <IconButton
          onClick={onCancel}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "#fff",
            bgcolor: "rgba(255,255,255,0.15)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.28)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            src={form.contact_image || undefined}
            sx={{
              width: 56,
              height: 56,
              border: "2px solid rgba(255,255,255,0.6)",
              bgcolor: "rgba(255,255,255,0.15)",
            }}
          >
            {!form.contact_image && <PersonOutline />}
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {form.first_name || form.last_name
                ? `${form.first_name} ${form.last_name}`.trim()
                : "Edit Contact"}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.25 }}>
              {form.role || "No role set"}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Form body ── */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ px: 4, py: 4, maxHeight: "70vh", overflowY: "auto" }}
      >
        <Box mb={4}>
          <SectionHeader
            icon={<PersonOutline sx={{ fontSize: 18 }} />}
            title="Personal Details"
            accent={accent}
          />
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="First Name"
                name="first_name"
                value={form.first_name}
                onChange={handleChange}
                required
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={form.last_name}
                onChange={handleChange}
                required
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Role"
                name="role"
                placeholder="e.g. Site Manager, Owner"
                value={form.role ?? ""}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <RoleIcon
                        sx={{ fontSize: 18, color: colors.primary[300] }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Email"
                name="email"
                value={form.email}
                onChange={handleChange}
                error={!!emailError}
                helperText={emailError}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {isEmailValid && <Check sx={{ color: accent }} />}
                    </InputAdornment>
                  ),
                }}
                sx={{
                  ...textFieldStyles,
                  "& .MuiFormHelperText-root": {
                    color: colors.neutral[100],
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <PhoneNumberField
                fullWidth
                label="Phone Number"
                value={form.phone}
                onChange={handlePhoneInputChange}
                defaultCountry="ZW"
                variant="outlined"
                sx={{ marginBottom: "15px" }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Website"
                name="website"
                value={form.website}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <PhotoEditor
                initialImage={form.contact_image}
                title="Profile Photo"
                onSave={(imageData: string) =>
                  handleSaveContactImage(imageData)
                }
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 4, borderColor: colors.primary[300] }} />

        <Box mb={4}>
          <SectionHeader
            icon={<LocationOn sx={{ fontSize: 18 }} />}
            title="Location Info"
            accent={accent}
          />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Address Line 1"
                name="address_one"
                value={form.address_one}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Address Line 2"
                name="address_two"
                value={form.address_two}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="City"
                name="city"
                value={form.city}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Suburb"
                name="suburb"
                value={form.suburb}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Country"
                name="country"
                value={form.country}
                onChange={handleSelectChangeWrapper}
                select
                sx={textFieldStyles}
              >
                <MenuItem value="" disabled sx={{ color: "#aaa" }}>
                  Select Country
                </MenuItem>
                <MenuItem value="Zimbabwe">Zimbabwe</MenuItem>
                <MenuItem value="South Africa">South Africa</MenuItem>
                <MenuItem value="Zambia">Zambia</MenuItem>
                <MenuItem value="Malawi">Malawi</MenuItem>
                <MenuItem value="Botswana">Botswana</MenuItem>
                <MenuItem value="Namibia">Namibia</MenuItem>
              </CustomTextField>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 4, borderColor: colors.primary[300] }} />

        <Box mb={4}>
          <SectionHeader
            icon={<Public sx={{ fontSize: 18 }} />}
            title="Social Media"
            accent={accent}
          />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Facebook Profile"
                name="facebook_profile"
                value={form.facebook_profile}
                placeholder="https://"
                onChange={handleChange}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="LinkedIn Profile"
                name="linkedin_profile"
                value={form.linkedin_profile}
                placeholder="https://"
                onChange={handleChange}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="X Profile"
                name="twitter_profile"
                value={form.twitter_profile}
                placeholder="https://"
                onChange={handleChange}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Instagram Profile"
                name="instagram_profile"
                value={form.instagram_profile}
                placeholder="https://"
                onChange={handleChange}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 4, borderColor: colors.primary[300] }} />

        <Box mb={1}>
          <SectionHeader
            icon={<StickyNote2 sx={{ fontSize: 18 }} />}
            title="Additional Info"
            accent={accent}
          />
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <CustomTextField
                fullWidth
                label="Notes"
                name="notes"
                multiline
                rows={4}
                value={form.notes}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
          </Grid>
        </Box>

        {/* ── Sticky footer actions ── */}
        <Box
          sx={{
            position: "sticky",
            bottom: -32,
            mt: 4,
            mx: -4,
            px: 4,
            py: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            bgcolor: colors.neutral[900],
            borderTop: "1px solid",
            borderColor: colors.primary[300],
          }}
        >
          <Button
            variant="outlined"
            onClick={() => onCancel()}
            disabled={isSubmitting}
            sx={{
              borderColor: accent,
              color: accent,
              borderRadius: "10px",
              textTransform: "none",
              px: 3,
              "&:hover": {
                borderColor: colors.primary[300],
                backgroundColor: colors.primary[50],
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: accent,
              borderRadius: "10px",
              textTransform: "none",
              px: 3,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: colors.primary[300],
                boxShadow: "none",
              },
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={22} sx={{ color: colors.neutral[100] }} />
            ) : (
              "Save Contact"
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default DialogEditVenueIndividualContacts;
